import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { AppHeader } from "../components/AppHeader";
import { fmtDate, barStatusLabel } from "../components/ChapterRow";
import { ArrowLeft, AlertTriangle, History, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { toast, Toaster } from "sonner";
import LanguageGroupsView from "../components/LanguageGroupsView";

const STATUS_META = {
  vert: { label: "Dans les temps", dot: "bg-[var(--ok)]", text: "text-[var(--ok)]" },
  jaune: { label: "Retard (1-14 j)", dot: "bg-[var(--just)]", text: "text-[var(--just)]" },
  rouge: { label: "Retard important (>14 j)", dot: "bg-[var(--late)]", text: "text-[var(--late)]" },
  termine: { label: "Programme terminé", dot: "bg-[var(--ok)]", text: "text-[var(--ok)]" },
  aucun: { label: "Aucun chapitre en cours", dot: "bg-[var(--muted)]", text: "text-[var(--muted)]" },
};

const AUDIT_ACTION_LABELS = {
  correct_dates: "Correction des dates réelles",
  reopen: "Réouverture du chapitre",
  start_for_teacher: "Démarrage à la place du professeur",
  end_for_teacher: "Clôture à la place du professeur",
  update_plan: "Modification du prévisionnel",
  move: "Réordonnancement",
};

const rowStatus = (r) => {
  if (r.current_chapter) return r.current_chapter.bar?.color || "vert";
  if (r.total > 0 && r.ended_count === r.total) return "termine";
  return "aucun";
};

function AlertBanner({ rows }) {
  const late = rows.filter((r) => ["jaune", "rouge"].includes(rowStatus(r)));
  if (!late.length) return null;
  const sorted = [...late].sort((a, b) => (b.current_chapter?.bar?.delay || 0) - (a.current_chapter?.bar?.delay || 0));
  return (
    <div className="mt-8 space-y-2" data-testid="progression-alerts">
      <div className="eyebrow flex items-center gap-2"><AlertTriangle size={14} /> Alertes de retard ({late.length})</div>
      {sorted.map((r) => {
        const bar = r.current_chapter.bar;
        const red = bar.color === "rouge";
        return (
          <div
            key={r.assignment_id}
            className={`border px-4 py-3 text-sm flex flex-wrap items-center gap-2 ${red ? "bg-[var(--late)]/10 border-[var(--late)]" : "bg-[var(--just)]/10 border-[var(--just)]"}`}
            data-testid={`alert-${r.assignment_id}`}
          >
            <span className={`font-bold ${red ? "text-[var(--late)]" : "text-[var(--just)]"}`}>
              {red ? "RETARD IMPORTANT" : "Progression en retard"}
            </span>
            <span>
              — {r.teacher?.name} — {r.subject?.name} — {r.class?.name} — {r.current_chapter.title} — {bar.delay} jour{bar.delay > 1 ? "s" : ""} de retard
            </span>
          </div>
        );
      })}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="text-xs">
      <span className="eyebrow block mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-3 py-2 bg-white border border-[var(--parchment-2)] focus:border-[var(--maroon)] outline-none text-sm min-w-[140px]"
        data-testid={testId}
      >
        <option value="">Tous</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function finalDelayLabel(d) {
  if (d > 0) return { text: `+${d} jour${d > 1 ? "s" : ""} de retard`, cls: "text-[var(--late)] font-medium" };
  if (d < 0) return { text: `${-d} jour${-d > 1 ? "s" : ""} d'avance`, cls: "text-[var(--ok)] font-medium" };
  return { text: "Terminé dans les temps", cls: "text-[var(--ok)] font-medium" };
}

function HistoryChapterStatus({ chapter }) {
  if (chapter.ended_at) {
    const meta = finalDelayLabel(chapter.delay_days);
    return <span className={meta.cls}>{meta.text}</span>;
  }
  if (chapter.bar) {
    return <span className={`font-medium ${STATUS_META[chapter.bar.color].text}`}>{barStatusLabel(chapter.bar)}</span>;
  }
  return <span className="text-[var(--muted)]">À venir</span>;
}

function HistoryRows({ row }) {
  return (
    <tr>
      <td colSpan={9} className="bg-[var(--parchment)]/40 px-4 py-3">
        <div className="text-xs space-y-1.5" data-testid={`history-${row.assignment_id}`}>
          {row.history.length === 0 && <span className="italic text-[var(--muted)]">Aucun chapitre au programme.</span>}
          {row.history.map((c) => (
            <div key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-mono-spec text-[var(--muted)]">Ch. {c.order}</span>
              <span className="font-medium">{c.title}</span>
              <span className="text-[var(--ink-soft)]">
                Prévu : {c.start_date ? fmtDate(c.start_date) : "—"} → {c.end_date ? fmtDate(c.end_date) : "—"}
              </span>
              <span className="text-[var(--ink-soft)]">
                Réel : {c.started_at ? fmtDate(c.started_at) : "—"} → {c.ended_at ? fmtDate(c.ended_at) : "—"}
              </span>
              <HistoryChapterStatus chapter={c} />
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
}

const fmtAuditVal = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return fmtDate(v);
  return String(v);
};

const fmtAuditObj = (obj) => (obj ? Object.entries(obj).map(([k, v]) => `${k}: ${fmtAuditVal(v)}`).join(" · ") : "—");

function AuditTable({ entries }) {
  if (!entries?.length) {
    return <p className="text-sm text-[var(--ink-soft)] italic">Aucune intervention enregistrée.</p>;
  }
  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-left text-[var(--muted)] border-b border-[var(--parchment-2)]">
          <th className="py-2 pr-3">Date · heure</th>
          <th className="py-2 pr-3">Utilisateur</th>
          <th className="py-2 pr-3">Action</th>
          <th className="py-2 pr-3">Chapitre</th>
          <th className="py-2 pr-3">Ancienne valeur</th>
          <th className="py-2">Nouvelle valeur</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id} className="border-b border-[var(--parchment)]">
            <td className="py-2 pr-3 font-mono-spec whitespace-nowrap">{new Date(e.at).toLocaleString("fr-FR")}</td>
            <td className="py-2 pr-3">{e.user_name}</td>
            <td className="py-2 pr-3">{AUDIT_ACTION_LABELS[e.action] || e.action}</td>
            <td className="py-2 pr-3">{e.chapter_title}{e.subject_name ? ` · ${e.subject_name}` : ""}{e.class_name ? ` · ${e.class_name}` : ""}</td>
            <td className="py-2 pr-3 text-[var(--ink-soft)]">{fmtAuditObj(e.old)}</td>
            <td className="py-2">{fmtAuditObj(e.new)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AuditJournal() {
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState(null);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && entries === null) {
      const { data } = await api.get("/admin/chapters-audit");
      setEntries(data);
    }
  };

  return (
    <div className="mt-12 bg-white border border-[var(--parchment-2)]">
      <button onClick={toggle} className="w-full px-5 py-4 flex items-center justify-between text-sm font-medium" data-testid="audit-journal-toggle">
        <span className="flex items-center gap-2"><History size={16} /> Journal des interventions administratives</span>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="px-5 pb-5 overflow-x-auto" data-testid="audit-journal">
          <AuditTable entries={entries} />
        </div>
      )}
    </div>
  );
}

function CurrentChapterCell({ row }) {
  if (row.current_chapter) {
    return (
      <Link to={`/admin/assignment/${row.assignment_id}`} className="hover:text-[var(--maroon)] underline decoration-dotted">
        {row.current_chapter.title}
      </Link>
    );
  }
  if (row.total > 0 && row.ended_count === row.total) {
    return <span className="text-[var(--ok)]">Programme terminé ({row.ended_count}/{row.total})</span>;
  }
  if (row.next_chapter) {
    return <span className="text-[var(--muted)] italic">Prochain : {row.next_chapter.title}</span>;
  }
  return <span className="text-[var(--muted)] italic">Aucun programme saisi</span>;
}

function DelayCell({ current }) {
  if (!current) return <>—</>;
  if (current.bar?.delay) {
    const red = current.bar.color === "rouge";
    return (
      <span className={`font-bold ${red ? "text-[var(--late)]" : "text-[var(--just)]"}`}>
        {current.bar.delay} j
      </span>
    );
  }
  return <span className="text-[var(--ok)] text-xs">Dans les temps</span>;
}

const filterRows = (rows, filters) =>
  rows.filter((r) => {
    if (filters.teacher && r.teacher?.id !== filters.teacher) return false;
    if (filters.subject && r.subject?.id !== filters.subject) return false;
    if (filters.klass && r.class?.id !== filters.klass) return false;
    if (filters.level && r.class?.level !== filters.level) return false;
    if (filters.status && rowStatus(r) !== filters.status) return false;
    return true;
  });

function ProgressionsFilters({ filters, setFilters, options, count }) {
  const set = (k) => (v) => setFilters((f) => ({ ...f, [k]: v }));
  return (
    <div className="mt-8 flex flex-wrap items-end gap-4 bg-white border border-[var(--parchment-2)] p-4" data-testid="progressions-filters">
      <FilterSelect label="Professeur" value={filters.teacher} onChange={set("teacher")} options={options.teachers} testId="filter-teacher" />
      <FilterSelect label="Matière" value={filters.subject} onChange={set("subject")} options={options.subjects} testId="filter-subject" />
      <FilterSelect label="Classe" value={filters.klass} onChange={set("klass")} options={options.classes} testId="filter-class" />
      <FilterSelect label="Niveau" value={filters.level} onChange={set("level")} options={options.levels} testId="filter-level" />
      <FilterSelect
        label="Statut"
        value={filters.status}
        onChange={set("status")}
        options={Object.entries(STATUS_META).map(([value, m]) => ({ value, label: m.label }))}
        testId="filter-status"
      />
      <span className="text-xs text-[var(--muted)] ml-auto" data-testid="progressions-count">{count} progression{count > 1 ? "s" : ""}</span>
    </div>
  );
}

function RowActions({ r, isOpen, onToggle, onReopen }) {
  const lastEnded = [...r.history].reverse().find((c) => c.ended_at);
  return (
    <div className="inline-flex items-center gap-1.5">
      {!r.current_chapter && lastEnded && (
        <button
          onClick={() => onReopen(lastEnded.id)}
          className="btn-secondary !py-1 !px-2 text-xs"
          title="Rouvrir le dernier chapitre clôturé"
          data-testid={`reopen-btn-${r.assignment_id}`}
        >
          <RotateCcw size={12} />
        </button>
      )}
      <button
        onClick={onToggle}
        className="btn-secondary !py-1 !px-2 text-xs"
        title="Historique des chapitres"
        data-testid={`history-toggle-${r.assignment_id}`}
      >
        {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />} {r.ended_count}/{r.total}
      </button>
    </div>
  );
}

function ProgressionRow({ r, isOpen, onToggle, onReopen }) {
  const meta = STATUS_META[rowStatus(r)];
  const cur = r.current_chapter;
  return (
    <>
      <tr className="border-b border-[var(--parchment)] hover:bg-[var(--parchment)]/30" data-testid={`progression-row-${r.assignment_id}`}>
        <td className="px-4 py-3 whitespace-nowrap">
          <span className={`inline-flex items-center gap-2 text-xs font-medium ${meta.text}`}>
            <span className={`w-2.5 h-2.5 ${meta.dot}`} /> {meta.label}
          </span>
        </td>
        <td className="px-4 py-3">{r.teacher?.name}</td>
        <td className="px-4 py-3">{r.subject?.name}</td>
        <td className="px-4 py-3 whitespace-nowrap">{r.class?.name}</td>
        <td className="px-4 py-3"><CurrentChapterCell row={r} /></td>
        <td className="px-4 py-3 font-mono-spec text-xs whitespace-nowrap">{cur?.started_at ? fmtDate(cur.started_at) : "—"}</td>
        <td className="px-4 py-3 font-mono-spec text-xs whitespace-nowrap">{cur?.end_date ? fmtDate(cur.end_date) : "—"}</td>
        <td className="px-4 py-3 whitespace-nowrap"><DelayCell current={cur} /></td>
        <td className="px-4 py-3 whitespace-nowrap text-right">
          <RowActions r={r} isOpen={isOpen} onToggle={onToggle} onReopen={onReopen} />
        </td>
      </tr>
      {isOpen && <HistoryRows row={r} />}
    </>
  );
}

function ProgressionsTable({ rows, expanded, setExpanded, onReopen }) {
  return (
    <div className="mt-4 bg-white border border-[var(--parchment-2)] overflow-x-auto">
      <table className="w-full text-sm" data-testid="progressions-table">
        <thead>
          <tr className="text-left text-xs text-[var(--muted)] border-b border-[var(--parchment-2)]">
            <th className="px-4 py-3">Statut</th>
            <th className="px-4 py-3">Professeur</th>
            <th className="px-4 py-3">Matière</th>
            <th className="px-4 py-3">Classe</th>
            <th className="px-4 py-3">Chapitre en cours</th>
            <th className="px-4 py-3">Début réel</th>
            <th className="px-4 py-3">Fin prévue</th>
            <th className="px-4 py-3">Retard</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <ProgressionRow
              key={r.assignment_id}
              r={r}
              isOpen={expanded === r.assignment_id}
              onToggle={() => setExpanded(expanded === r.assignment_id ? null : r.assignment_id)}
              onReopen={onReopen}
            />
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={9} className="px-4 py-8 text-center text-[var(--ink-soft)] italic">Aucune progression ne correspond aux filtres.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const buildOptions = (rows) => {
  const uniq = (arr) => [...new Map(arr.filter((o) => o.value).map((o) => [o.value, o])).values()].sort((a, b) => a.label.localeCompare(b.label));
  return {
    teachers: uniq(rows.map((r) => ({ value: r.teacher?.id, label: r.teacher?.name }))),
    subjects: uniq(rows.map((r) => ({ value: r.subject?.id, label: r.subject?.name }))),
    classes: uniq(rows.map((r) => ({ value: r.class?.id, label: r.class?.name }))),
    levels: uniq(rows.map((r) => ({ value: r.class?.level, label: r.class?.level }))),
  };
};

function useProgressions() {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/progressions");
    setData(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const options = useMemo(() => buildOptions(rows), [rows]);

  const reopenChapter = async (chapterId) => {
    try {
      await api.post(`/admin/chapters/${chapterId}/reopen`);
      toast.success("Chapitre rouvert");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return { data, rows, options, reopenChapter };
}

export default function AdminProgressions() {
  const [filters, setFilters] = useState({ teacher: "", subject: "", klass: "", level: "", status: "" });
  const [expanded, setExpanded] = useState(null);
  const { data, rows, options, reopenChapter } = useProgressions();
  const filtered = filterRows(rows, filters);

  return (
    <div className="min-h-screen bg-[var(--cream)]">
      <AppHeader />
      <Toaster richColors position="top-right" />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-[var(--ink-soft)] hover:text-[var(--maroon)] transition-colors" data-testid="back-link">
          <ArrowLeft size={16} /> Retour au tableau de bord
        </Link>
        <div className="mt-6">
          <div className="eyebrow">Direction</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl font-semibold mt-2">Suivi des progressions</h1>
          <p className="mt-3 text-[var(--ink-soft)] max-w-2xl">
            Chapitre en cours, retard et prochain chapitre pour chaque professeur × matière × classe.
          </p>
        </div>

        {!data ? (
          <div className="py-12 text-[var(--ink-soft)]">Chargement...</div>
        ) : (
          <>
            <AlertBanner rows={rows} />

            <ProgressionsFilters filters={filters} setFilters={setFilters} options={options} count={filtered.length} />

            <ProgressionsTable rows={filtered} expanded={expanded} setExpanded={setExpanded} onReopen={reopenChapter} />

            <LanguageGroupsView />

            <AuditJournal />
          </>
        )}
      </main>
    </div>
  );
}
